import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import MarketingNav from '../components/layout/MarketingNav';
import { Button, Card, Input } from '../components/ui';
import AgreementModal from '../components/AgreementModal';
import WellnessAreaSelector from '../components/WellnessAreaSelector';
import api from '../services/api';

const EyeIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const EyeOffIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
  </svg>
);

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    title: '',
    phone: '',
    smsConsent: false,
    role: 'client',
    coachSubtype: 'life_coach',
    referredBy: '',
    licenseNumber: '',
    licenseType: '',
    customLicenseType: '',
    licenseState: '',
    licenseExpiration: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [wellnessStep, setWellnessStep] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (formData.role === 'clinician') {
      const finalLicenseType = formData.licenseType === 'Other' ? formData.customLicenseType : formData.licenseType;
      if (!formData.licenseNumber || !finalLicenseType || !formData.licenseState || !formData.licenseExpiration) {
        setError('Clinician registration requires license type, number, state, and expiration.');
        return;
      }
      if (formData.licenseType === 'Other' && !formData.customLicenseType.trim()) {
        setError('Please specify your license type.');
        return;
      }
    }

    if (formData.role === 'coach' && !formData.coachSubtype) {
      setError('Please choose a coaching subtype.');
      return;
    }

    // Show agreement modal instead of registering immediately
    setShowAgreementModal(true);
  };

  const handleAgreementAccept = async (agreementResponse) => {
    setLoading(true);
    setError('');

    try {
      // First, register the user
      const { confirmPassword, customLicenseType, ...registrationData } = formData;
      // Use custom license type if "Other" was selected
      if (registrationData.licenseType === 'Other') {
        registrationData.licenseType = formData.customLicenseType.trim();
      }
      const result = await register(registrationData);

      if (!result.success) {
        setError(result.message);
        setShowAgreementModal(false);
        setLoading(false);
        return;
      }

      // Then acknowledge the agreement
      try {
        await api.post('/agreements/acknowledge', agreementResponse);

        // Redirect based on user role
        if (result.user?.role === 'admin') {
          navigate('/admin');
        } else if (result.user?.role === 'client') {
          // Client pathway — offer the optional wellness-area step before dashboard
          setShowAgreementModal(false);
          setWellnessStep({ userId: result.user._id, pathway: result.user.planTier || 'self' });
          setLoading(false);
          return;
        } else {
          navigate('/dashboard');
        }
      } catch (agreementError) {
        console.error('Agreement acknowledgement failed:', agreementError);
        // User is created but agreement failed - redirect anyway with warning
        setError('Account created but agreement acknowledgement failed. Please contact support.');
        setTimeout(() => {
          navigate('/dashboard');
        }, 3000);
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
      setShowAgreementModal(false);
    } finally {
      setLoading(false);
    }
  };

  const handleAgreementCancel = () => {
    setShowAgreementModal(false);
    navigate('/login');
  };

  if (wellnessStep) {
    return (
      <>
        <MarketingNav transparent={false} />
        <main className="flex min-h-screen items-start justify-center bg-neutral-50 px-6 py-12 mt-20">
          <div className="w-full max-w-3xl">
            <Card>
              <div className="text-center">
                <p className="text-sm font-semibold uppercase tracking-wide text-primary-600">One more step</p>
                <h1 className="mt-2 text-3xl font-display font-semibold text-neutral-900">What brings you here?</h1>
                <p className="mt-2 text-neutral-500">
                  Pick up to 5 focus areas. We will match your homework to what you are working on — you can change these any time in your profile.
                </p>
                <p className="mt-1 text-sm text-neutral-400">
                  Your clinician can also update these on your behalf.
                </p>
              </div>
              <div className="mt-6">
                <WellnessAreaSelector
                  userId={wellnessStep.userId}
                  currentAreas={[]}
                  onSave={() => navigate('/dashboard')}
                />
              </div>
              <div className="mt-4 text-center">
                <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
                  Skip for now
                </Button>
              </div>
            </Card>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <MarketingNav transparent={false} />
      <main className="flex min-h-screen items-center justify-center bg-neutral-50 px-6 py-12 mt-20">
      <div className="w-full max-w-2xl">
        <Card>
          <div className="text-center">
            <p className="text-sm font-semibold uppercase tracking-wide text-primary-600">MHDF Momentum</p>
            <h1 className="mt-2 text-3xl font-display font-semibold text-neutral-900">Create Account</h1>
            <p className="mt-2 text-neutral-500">Join the program and track your daily progress.</p>
          </div>

          {error && (
            <div className="mt-4 rounded-soft border border-error-200 bg-error-50 p-3 text-sm text-error-700">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              <label className="block text-sm text-neutral-700">
                <span className="mb-2 block font-medium">Title (Optional)</span>
                <select
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                >
                  <option value="">None</option>
                  <option value="Mr.">Mr.</option>
                  <option value="Mrs.">Mrs.</option>
                  <option value="Ms.">Ms.</option>
                  <option value="Dr.">Dr.</option>
                  <option value="Prof.">Prof.</option>
                  <option value="Rev.">Rev.</option>
                </select>
              </label>
              <Input
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                fullWidth
                required
              />
              <Input
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                fullWidth
                required
              />
            </div>

            <Input
              label="Username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              helperText="3-30 characters (letters, numbers, dots, underscores, hyphens)"
              fullWidth
              required
            />

            <Input
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              fullWidth
              required
            />

            <Input
              label="Phone Number (Optional for SMS)"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+1 (555) 123-4567"
              helperText="For SMS wellness reminders and alerts"
              fullWidth
            />

            {formData.phone && (
              <label className="flex items-start gap-3 rounded-soft border-2 border-primary-200 bg-primary-50 p-4 cursor-pointer hover:bg-primary-100 transition">
                <input
                  type="checkbox"
                  name="smsConsent"
                  checked={formData.smsConsent}
                  onChange={(e) => setFormData({ ...formData, smsConsent: e.target.checked })}
                  className="mt-0.5 h-5 w-5 rounded border-primary-300 text-primary-600 focus:ring-primary-500"
                />
                <div className="text-sm text-primary-900">
                  <p className="font-semibold">I agree to receive SMS notifications from MHDF Momentum.</p>
                  <p className="mt-1 text-xs text-primary-700">
                    By checking this box, you consent to receive text messages including daily reminders, 
                    milestone celebrations, and clinician alerts. Message & data rates may apply. 
                    You can reply <strong>STOP</strong> to opt out at any time or manage preferences in your account settings.
                  </p>
                </div>
              </label>
            )}

            <Input
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              helperText="At least 8 characters"
              endIcon={showPassword ? EyeOffIcon : EyeIcon}
              onEndIconClick={() => setShowPassword(!showPassword)}
              fullWidth
              required
            />

            <Input
              label="Confirm Password"
              name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleChange}
              endIcon={showConfirmPassword ? EyeOffIcon : EyeIcon}
              onEndIconClick={() => setShowConfirmPassword(!showConfirmPassword)}
              fullWidth
              required
            />

            <label className="block text-sm text-neutral-700">
              <span className="mb-2 block font-medium">I am a...</span>
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
              >
                <option value="client">Client</option>
                <option value="clinician">Clinician</option>
                <option value="clergy">Clergy</option>
                <option value="coach">Coach</option>
              </select>
            </label>

            {formData.role === 'client' && (
              <Input
                label="Coach Referral Code (Optional)"
                name="referredBy"
                value={formData.referredBy}
                onChange={handleChange}
                helperText="Enter a coach referral code if you were invited by a coach"
                fullWidth
              />
            )}

            {formData.role === 'clinician' && (
              <div className="space-y-4">
                <Input
                  label="License Number"
                  name="licenseNumber"
                  value={formData.licenseNumber}
                  onChange={handleChange}
                  fullWidth
                  required
                />
                <label className="block text-sm text-neutral-700">
                  <span className="mb-2 block font-medium">License Type</span>
                  <select
                    name="licenseType"
                    value={formData.licenseType}
                    onChange={handleChange}
                    className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                    required
                  >
                    <option value="">Select license type</option>
                    <option value="LPC">Licensed Professional Counselor (LPC)</option>
                    <option value="LPC-S">Licensed Professional Counselor-Supervisor (LPC-S)</option>
                    <option value="LCSW">Licensed Clinical Social Worker (LCSW)</option>
                    <option value="LCSW-S">Licensed Clinical Social Worker-Supervisor (LCSW-S)</option>
                    <option value="LMFT">Licensed Marriage and Family Therapist (LMFT)</option>
                    <option value="LMFT-S">Licensed Marriage and Family Therapist-Supervisor (LMFT-S)</option>
                    <option value="LCDC">Licensed Chemical Dependency Counselor (LCDC)</option>
                    <option value="PsyD">Doctor of Psychology (Psy D)</option>
                    <option value="Psychiatrist">Psychiatrist</option>
                    <option value="Psychologist">Psychologist</option>
                    <option value="MD">Medical Doctor (MD)</option>
                    <option value="PA">Physician Assistant (PA)</option>
                    <option value="NP">Nurse Practitioner (NP)</option>
                    <option value="Life Coach">Life Coach</option>
                    <option value="Other">Other (Specify)</option>
                  </select>
                </label>
                {formData.licenseType === 'Other' && (
                  <Input
                    label="Specify License Type"
                    name="customLicenseType"
                    value={formData.customLicenseType}
                    onChange={handleChange}
                    placeholder="Enter your license type"
                    fullWidth
                    required
                  />
                )}
                <label className="block text-sm text-neutral-700">
                  <span className="mb-2 block font-medium">License State</span>
                  <select
                    name="licenseState"
                    value={formData.licenseState}
                    onChange={handleChange}
                    className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                    required
                  >
                    <option value="">Select state</option>
                    <option value="AL">Alabama (AL)</option>
                    <option value="AK">Alaska (AK)</option>
                    <option value="AZ">Arizona (AZ)</option>
                    <option value="AR">Arkansas (AR)</option>
                    <option value="CA">California (CA)</option>
                    <option value="CO">Colorado (CO)</option>
                    <option value="CT">Connecticut (CT)</option>
                    <option value="DE">Delaware (DE)</option>
                    <option value="DC">Washington D.C. (DC)</option>
                    <option value="FL">Florida (FL)</option>
                    <option value="GA">Georgia (GA)</option>
                    <option value="HI">Hawaii (HI)</option>
                    <option value="ID">Idaho (ID)</option>
                    <option value="IL">Illinois (IL)</option>
                    <option value="IN">Indiana (IN)</option>
                    <option value="IA">Iowa (IA)</option>
                    <option value="KS">Kansas (KS)</option>
                    <option value="KY">Kentucky (KY)</option>
                    <option value="LA">Louisiana (LA)</option>
                    <option value="ME">Maine (ME)</option>
                    <option value="MD">Maryland (MD)</option>
                    <option value="MA">Massachusetts (MA)</option>
                    <option value="MI">Michigan (MI)</option>
                    <option value="MN">Minnesota (MN)</option>
                    <option value="MS">Mississippi (MS)</option>
                    <option value="MO">Missouri (MO)</option>
                    <option value="MT">Montana (MT)</option>
                    <option value="NE">Nebraska (NE)</option>
                    <option value="NV">Nevada (NV)</option>
                    <option value="NH">New Hampshire (NH)</option>
                    <option value="NJ">New Jersey (NJ)</option>
                    <option value="NM">New Mexico (NM)</option>
                    <option value="NY">New York (NY)</option>
                    <option value="NC">North Carolina (NC)</option>
                    <option value="ND">North Dakota (ND)</option>
                    <option value="OH">Ohio (OH)</option>
                    <option value="OK">Oklahoma (OK)</option>
                    <option value="OR">Oregon (OR)</option>
                    <option value="PA">Pennsylvania (PA)</option>
                    <option value="RI">Rhode Island (RI)</option>
                    <option value="SC">South Carolina (SC)</option>
                    <option value="SD">South Dakota (SD)</option>
                    <option value="TN">Tennessee (TN)</option>
                    <option value="TX">Texas (TX)</option>
                    <option value="UT">Utah (UT)</option>
                    <option value="VT">Vermont (VT)</option>
                    <option value="VA">Virginia (VA)</option>
                    <option value="WA">Washington (WA)</option>
                    <option value="WV">West Virginia (WV)</option>
                    <option value="WI">Wisconsin (WI)</option>
                    <option value="WY">Wyoming (WY)</option>
                  </select>
                </label>
                <Input
                  label="License Expiration"
                  name="licenseExpiration"
                  type="date"
                  value={formData.licenseExpiration}
                  onChange={handleChange}
                  fullWidth
                  required
                />
              </div>
            )}
            {formData.role === 'clergy' && (
              <div className="rounded-soft border border-neutral-200 bg-neutral-50 p-3 text-sm text-neutral-600">
                Clergy accounts focus on encouragement and engagement signals. Clinical credentials are not required.
              </div>
            )}

            {formData.role === 'coach' && (
              <div className="space-y-4">
                <label className="block text-sm text-neutral-700">
                  <span className="mb-2 block font-medium">Coaching Subtype</span>
                  <select
                    name="coachSubtype"
                    value={formData.coachSubtype}
                    onChange={handleChange}
                    className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                    required
                  >
                    <option value="life_coach">Life Coach</option>
                    <option value="business_coach">Business Coach</option>
                  </select>
                </label>
                <div className="rounded-soft border border-neutral-200 bg-neutral-50 p-3 text-sm text-neutral-600">
                  Coach accounts can view engagement trends and manage goal boards for assigned participants only.
                </div>
              </div>
            )}

            <Button type="submit" fullWidth disabled={loading} className="w-full">
              {loading ? 'Creating Account...' : 'Sign Up'}
            </Button>
          </form>

          <p className="mt-4 text-center text-sm text-neutral-500">
            Already have an account?{' '}
            <Link to="/login" className="font-semibold text-primary-600 hover:text-primary-700">
              Sign In
            </Link>
          </p>
        </Card>

        {/* Agreement Modal */}
        <AgreementModal
          agreementType={formData.role}
          isOpen={showAgreementModal}
          onAccept={handleAgreementAccept}
          onCancel={handleAgreementCancel}
        />
      </div>
    </main>
    </>
  );
};

export default Register;
